import { computed, inject, Injectable, signal, WritableSignal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

import { ProjectBloc } from '@connect-services/client/blocs/project.bloc';
import { FetchPictureService } from '@connect-services/client/services/fetch-picture.service';
import { Deposit, Standard } from '@connect-services/src/sdk/api';

export class Measurements {
    public blocHeight!: number;
    public cartouchePadding!: number;
    public cartoucheWidth!: number;
    public marginText!: number;
    public marginTitleX!: number;
    public rowLogoDateWidth!: number;
    public rowProjectDataWidth!: number;
    public spaceBetweenTitleAndDescription!: number;
}

export class TextBlocCreator extends Measurements {
    public blocWidth!: number;
    public blocY!: number;
    public textX!: number;
}

const svgProperties = {
    bloc: { fill: 'white', stroke: 'black', 'stroke-width': 2 },
    text: { fill: 'black', 'font-family': 'DM Sans, sans-serif', 'font-size': '12' },
    title: { fill: 'black', 'font-family': 'DM Sans, sans-serif', 'font-size': '11' },
};

const commonMeasurements: Measurements = {
    blocHeight: 30,
    cartouchePadding: 10,
    cartoucheWidth: 440,
    marginText: 18,
    marginTitleX: 5,
    rowLogoDateWidth: 180,
    rowProjectDataWidth: 260,
    spaceBetweenTitleAndDescription: 56,
};

export type DepositWithStandard = Deposit & { standard: Standard };

@Injectable({
    providedIn: 'root',
})
export class ExportSynopticCartoucheService {
    public readonly blocAttributes: WritableSignal<string | null> = signal(null);
    public readonly projectName = computed(() => this.projectBloc.value()?.name || '');
    public readonly textAttributes: WritableSignal<string | null> = signal(null);
    public readonly titleAttributes: WritableSignal<string | null> = signal(null);

    private readonly connectServicesLogo: WritableSignal<string | null> = signal(null);
    private fetchPictureService = inject(FetchPictureService);
    private readonly projectBloc = inject(ProjectBloc);
    private readonly translocoService = inject(TranslocoService);

    public constructor() {
        this.titleAttributes.set(this.getTitleProperties());
        this.textAttributes.set(this.getTextProperties());
        this.blocAttributes.set(this.getBlocAttributes());

        this.getBase64ConnectServicesLogo().then((logo) => {
            this.connectServicesLogo.set(logo);
        });
    }

    public createSynopticCartouche(
        isFullSynoExport: boolean,
        deposit: DepositWithStandard,
        dimensions: { height: number; width: number },
        nodeName?: string,
    ): string {
        const truncateTextIfNeeded = (text: string, maxLength: number): string => {
            return text.length > maxLength ? `${text.slice(0, maxLength)}...` : text;
        };
        const depositText = `${truncateTextIfNeeded(deposit.name, 17)} - ${this.translocoService.translate('version')} n°${deposit.version}`;

        const measures: Measurements = commonMeasurements;

        const cartoucheX: number =
            dimensions.width - measures.rowProjectDataWidth - measures.rowLogoDateWidth - measures.cartouchePadding;

        const cartoucheXSecondCol: number = dimensions.width - measures.rowLogoDateWidth - measures.cartouchePadding;

        return `
            <!--Title-->
            ${this.createBlock(
                cartoucheX,
                this.getTitleMeasurements(isFullSynoExport),
                this.translocoService.translate('title'),
                isFullSynoExport
                    ? this.translocoService.translate('synopticExport.completeExportSynoptic')
                    : `${this.translocoService.translate(
                          'synopticExport.partialExportSynopticFromNode',
                      )} "${nodeName}"`,
            )}

             <!--Project-->
            ${this.createBlock(
                cartoucheX,
                this.getProjectMeasurements(),
                this.translocoService.translate('project'),
                this.projectName(),
            )}

            <!--Deposit-->
            ${this.createBlock(
                cartoucheX,
                this.getDepositMeasurements(),
                this.translocoService.translate('data'),
                depositText,
            )}

            <!--Date-->
            ${this.createBlock(
                cartoucheXSecondCol,
                this.getDateMeasurements(isFullSynoExport),
                this.translocoService.translate('synopticExport.exportDate'),
                new Date().toLocaleDateString(),
            )}

            <!--Logo-->
            ${this.createLogoBlock(isFullSynoExport, cartoucheXSecondCol, measures)}
          `;
    }

    public generateAttributes(properties: Record<string, string | number>): string {
        return Object.entries(properties)
            .map(([key, value]) => `${key}="${value}"`)
            .join(' ');
    }

    private createBlock(cartoucheX: number, measures: TextBlocCreator, title: string, text: string): string {
        return `
         <rect x="${cartoucheX}" y="${measures.blocY}" width="${measures.blocWidth}" height="${measures.blocHeight}" ${this.blocAttributes()} />
            <text x="${cartoucheX + measures.marginTitleX}" y="${measures.blocY + measures.marginText}" ${this.titleAttributes()} >
              ${title}
            </text>
            <text x="${cartoucheX + measures.spaceBetweenTitleAndDescription}" y="${measures.blocY + measures.marginText}" ${this.textAttributes()}>
              ${text}
            </text>
        `;
    }

    private createLogoBlock(isFullExportSyno: boolean, cartoucheX: number, measures: Measurements): string {
        return `
        <rect x="${cartoucheX}" y="${isFullExportSyno ? 35 : 65}" width="${measures.rowLogoDateWidth}" height="${isFullExportSyno ? measures.blocHeight * 2 : measures.blocHeight} " ${this.blocAttributes()}  />
             <image width="${isFullExportSyno ? measures.rowLogoDateWidth - measures.marginText : measures.rowLogoDateWidth / 2}" height="${isFullExportSyno ? measures.blocHeight * 2 - measures.marginText : measures.blocHeight}" x="${isFullExportSyno ? cartoucheX + 10 : cartoucheX + 40}" y="${isFullExportSyno ? 45 : 65}"  href="${this.connectServicesLogo()}" />

        `;
    }

    private getBase64ConnectServicesLogo(): Promise<string> {
        return this.fetchPictureService.fetchPicture('assets/images/logo/connect_services_logo_black.png');
    }

    private getBlocAttributes(): string {
        return this.generateAttributes(svgProperties.bloc);
    }

    private getDateMeasurements(isFullSynoExport: boolean): TextBlocCreator {
        return {
            ...commonMeasurements,
            blocWidth: commonMeasurements.rowLogoDateWidth,
            blocY: isFullSynoExport ? 5 : 35,
            spaceBetweenTitleAndDescription: 85,
            textX: commonMeasurements.rowProjectDataWidth + commonMeasurements.spaceBetweenTitleAndDescription,
        };
    }

    private getDepositMeasurements(): TextBlocCreator {
        return {
            ...commonMeasurements,
            blocWidth: commonMeasurements.rowProjectDataWidth,
            blocY: 65,
            textX: commonMeasurements.rowProjectDataWidth + commonMeasurements.spaceBetweenTitleAndDescription,
        };
    }

    private getProjectMeasurements(): TextBlocCreator {
        return {
            ...commonMeasurements,
            blocWidth: commonMeasurements.rowProjectDataWidth,
            blocY: 35,
            textX: commonMeasurements.rowProjectDataWidth + commonMeasurements.spaceBetweenTitleAndDescription,
        };
    }

    private getTextProperties(): string {
        return this.generateAttributes(svgProperties.text);
    }

    private getTitleMeasurements(isFullSynoExport: boolean): TextBlocCreator {
        return {
            ...commonMeasurements,
            blocWidth: isFullSynoExport ? commonMeasurements.rowProjectDataWidth : commonMeasurements.cartoucheWidth,
            blocY: 5,
            textX:
                (isFullSynoExport ? commonMeasurements.cartoucheWidth : commonMeasurements.rowProjectDataWidth) +
                commonMeasurements.spaceBetweenTitleAndDescription,
        };
    }

    private getTitleProperties(): string {
        return this.generateAttributes(svgProperties.title);
    }
}
